import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { IconNames } from 'pages/NewOverview/types';
import { colors } from '~/styles/colors';
import { HealthVariant } from './types';
import { UnitCard } from '~/pages/NewOverview/components/UnitCard';

interface UnitCardProps {
  value?: number;
  variant: HealthVariant;
  onClickDetails?: () => void;
  canSeeDetails?: boolean;
}

interface VariantConfig {
  cardColor: string;
  iconColor: string;
  icon: IconNames;
  /** Chave de tradução: o texto é traduzido na renderização, para acompanhar a troca de idioma. */
  descriptionKey: string;
  tooltipKey?: string;
}

const variantConfigs: Record<Exclude<HealthVariant, 'exception'>, VariantConfig> = {
  alert: {
    cardColor: colors.bg['warn-light'],
    iconColor: colors.state.warning.base,
    descriptionKey: 'unidadesEmAlerta',
    icon: 'WarnCircle',
  },
  critical: {
    cardColor: colors.bg['danger-light-0'],
    iconColor: colors.state.danger.base,
    descriptionKey: 'unidadesEmAlertaCritico',
    icon: 'XCircle1Icon',
  },
  healthy: {
    cardColor: colors.bg['success-light'],
    iconColor: colors.state.success.base,
    descriptionKey: 'unidadesSaudaveis',
    icon: 'CheckVerified1Icon',
  },
  inoperative: {
    cardColor: colors.bg['weak-100'],
    iconColor: colors.bg['soft-400'],
    descriptionKey: 'unidadesOffline',
    tooltipKey: 'unidadesOfflineTooltip',
    icon: 'SlashCircle1Icon',
  },
};

export function HealthUnitCard({
  variant,
  value,
  onClickDetails,
  canSeeDetails = true,
}: Readonly<UnitCardProps>): ReactElement {
  const { t } = useTranslation();
  const config = variantConfigs[variant];
  const size = variant === 'alert' || variant === 'inoperative' ? 16 : 22;

  return (
    <UnitCard
      cardColor={config.cardColor}
      description={t(config.descriptionKey)}
      tooltip={config.tooltipKey ? t(config.tooltipKey) : undefined}
      icon={{
        color: config.iconColor,
        name: config.icon,
        size,
      }}
      value={value}
      onClickDetails={canSeeDetails ? onClickDetails : undefined}
      disabledReasonKey={!canSeeDetails ? 'semUnidadesNesseStatus' : undefined}
    />
  );
}
